import React, { useEffect, useState } from 'react';
import BaseModal from '../baseModal';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { getNoneFungibleTokenBalance } from '../../services/balance';
import { authenticatedUserAddress } from '../../services/auth';
import { clientNetwork, deruptPfp } from '../../lib/constants';
import AvatarUpload from '../customavatar/AvatarUpload';
import { save } from '../../services/stacks';
import PngAvatar from '../customavatar/PngAvatar';
import Default from '../customavatar/Default';
import NftAvatar from '../customavatar/NftAvatar';

const AvatarConfigModal = ({
  show, close, setLoadOutData, loadOutData,
  setErrorMsg, setOpenSnackBar,
  storageUrl, setShowUserContractDeployModal,
  userContractStatus, setShowAdvisoryModal,
  setShowWoopsModal, setShowBannerUpload
}) => {
  const [nftBalance, setNftBalance] = useState({});
  const [index, setIndex] = useState(0); // Asset Selector
  const [assetId, setAssetId] = useState(0); // Asset Id Selector
  const [type, setType] = useState('default'); // Default type is 'default'
  const [url, setUrl] = useState(''); // URL for the avatar image
  const [finalLoadOut, setFinalLoadOut] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false); // New safeguard to track if loading is done

  const authUser = authenticatedUserAddress({ network: clientNetwork });

  // Handles setters for selected NFT asset and nft id's
  function handleSelectedNFTAsset(e) {
    const { name, value } = e.target;
    if (name === 'asset') {
      setAssetId(0);
      setIndex(value);
    }
    if (name === 'assetId') setAssetId(value);
    if (name === 'type') {
      setType(value);
      if (value === 'custom') setUrl('');
    }
  }

  // Handles loadout saving to gaia using authed user's app gaia save path
  async function saveLoadOut() {
    save({ fileName: deruptPfp, content: JSON.stringify(finalLoadOut) }, { encrypt: false }).then(({ success }) => {
      if (success) {
        console.log('done');
        setLoadOutData(finalLoadOut);
        setErrorMsg({ msg: 'PFP updated successfully.', reason: 'success' });
      } else {
        setErrorMsg({ msg: 'PFP update failed!!!', reason: 'warning' });
      }
      setOpenSnackBar(true);
      close();
    });
  }

  // Fetch user's derupt_pfp.json and initialize avatar configuration
  useEffect(() => {
    async function init() {
      try {
        // Fetch `derupt_pfp.json` from storage
        const response = await fetch(`${storageUrl}derupt_pfp.json`);
        if (response.ok) {
          const pfpData = await response.json();

          // Set type and URL based on `derupt_pfp.json`
          if (pfpData.type === 'custom' && pfpData.custom.img) {
            setType('custom');
            setUrl(pfpData.custom.img);
          } else if (pfpData.type === 'nft' && pfpData.nft.img) {
            setType('nft');
            setUrl(pfpData.nft.img);
          } else {
            setType('default');
            setUrl(''); // Clear URL for default
          }
        } else {
          // Fallback to loadOutData if no `derupt_pfp.json`
          setUrl(loadOutData?.nft?.img || loadOutData?.custom?.img || '');
          setType(loadOutData?.nft ? 'nft' : loadOutData?.custom ? 'custom' : 'default');
        }
      } catch (error) {
        console.error('Error fetching derupt_pfp.json:', error);
        setUrl(loadOutData?.nft?.img || loadOutData?.custom?.img || '');
        setType(loadOutData?.nft ? 'nft' : loadOutData?.custom ? 'custom' : 'default');
      } finally {
        setHasLoaded(true); // Set the flag indicating that the initial load is complete
      }

      // Retrieve the user's supported NFT balance from their principal
      const nftBal = await getNoneFungibleTokenBalance();
      setNftBalance(nftBal);
    }

    init();
  }, [storageUrl, loadOutData]);

  // URL state setter based on selected NFT, only updates if the type is already 'nft'
  useEffect(() => {
    // Only run this effect if the avatar has already loaded and the type is 'nft'
    if (hasLoaded && type === 'nft' && nftBalance.length > 0) {
      setUrl(`${nftBalance[index].imagehostprefix}${nftBalance[index].assetId[assetId].id}${nftBalance[index].imagehostsuffix}`);
    }
  }, [index, assetId, nftBalance, type, hasLoaded]); // Added hasLoaded dependency

  // User's loadOut Object setter
  useEffect(() => {
    setFinalLoadOut({
      type,
      nft: {
        contract: type === 'nft' ? nftBalance[index]?.contract : '',
        id: type === 'nft' ? nftBalance[index]?.assetId[assetId]?.id : '',
        img: type === 'nft' ? url : ''
      },
      custom: {
        img: type === 'custom' ? url : ''
      }
    });
  }, [type, index, assetId, nftBalance, url]);

  // Reset to default avatar when the "Default" button is clicked
  function resetToDefault() {
    setType('default');
    setUrl(''); // Clear the URL
  }

  return (
    <BaseModal show={show} handleClose={close}>
      <div className='avatarconfig-div'>
        <div className='avatarconfig-image'>
          {type === 'nft' && url && <NftAvatar url={url} />}
          {type === 'custom' && url && <PngAvatar authorPrincipal={authUser} url={url} />}
          {type === 'default' && <Default authorPrincipal={authUser} />}
        </div>

        {/* Type Selector */}
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Type</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={type}
            label="Type"
            name='type'
            onChange={handleSelectedNFTAsset}
          >
            <MenuItem value='nft'>Owned NFT</MenuItem>
            <MenuItem value='custom'>Upload Custom</MenuItem>
            <MenuItem value='default'>Default</MenuItem>
          </Select>
        </FormControl>

        {/* NFT Block */}
        {type === 'nft' && nftBalance.length > 0 && (
          <>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Collection"
              value={index}
              name='asset'
              onChange={handleSelectedNFTAsset}
              fullWidth
            >
              {nftBalance.map(({ label }, i) => (
                <MenuItem key={i} value={i}>{label}</MenuItem>
              ))}
            </Select>

            {nftBalance[index] && (
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={assetId}
                label="Id"
                name='assetId'
                onChange={handleSelectedNFTAsset}
                fullWidth
              >
                {nftBalance[index].assetId.map(({ id }, i) => (
                  <MenuItem key={i} value={i}>{id}</MenuItem>
                ))}
              </Select>
            )}
          </>
        )}

        {/* Custom Block */}
        {type === 'custom' && (
          <AvatarUpload
            setUrl={setUrl}
            setErrorMsg={setErrorMsg}
            setOpenSnackBar={setOpenSnackBar}
            setShowAdvisoryModal={setShowAdvisoryModal}
            setShowWoopsModal={setShowWoopsModal}
            userContractStatus={userContractStatus}
            setShowUserContractDeployModal={setShowUserContractDeployModal}
            setShowBannerUpload={setShowBannerUpload}
            storageUrl={storageUrl}
          />
        )}

        <div className="broadcast-modal-tx__buttons">
          <button className="cancelbutton" onClick={close}>Cancel</button>
          <button className="button" onClick={resetToDefault}>Default</button>
          <button className="button" onClick={saveLoadOut}>Save</button>
        </div>
      </div>
    </BaseModal>
  );
};

export default AvatarConfigModal;
